import { CellContext } from '@tanstack/react-table'
import { TableUiSchema, RowAction } from '../types-v4'
import { useSubmitForm } from '../../FormComponent/useSubmitForm'
import { SheetWithFormSchema } from '@/components/ui/SheetWithFormSchema'
import { DropdownMenuItem } from '@/components/shadcn/dropdown-menu'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { IconEditOutline } from '@icons/IconEditOutline'
import { InferFormValues, JsonFormEnumOptionV2 } from '../../FormComponent/types'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { isTitleValueObject, tableToFormSchema } from '../utils'
type EditItemProps<T, V> = {
  action: RowAction
  info: CellContext<T, V>
  tableSchema?: TableUiSchema
}

export function EditMenuItem<T, V>({ action, info, tableSchema }: EditItemProps<T, V>) {
  const existingEntry = { ...info.row.original }
  const { tileFilterSelections } = useTileFilters()

  // Default values for hooks to prevent conditional calls
  const handlerId = action.handlerId || ''
  const actionId = action.id || ''

  const { mutate: submitForm, isPending: isSubmitting } = useSubmitForm(handlerId, tileFilterSelections, actionId)

  const meta = info.table.options.meta as { updateGroup: (id: string, data: Record<string, unknown>) => void }
  const isGroup = (info.row.original as Record<string, unknown>).isGroup
  const updateGroup = (id: string, data: Record<string, unknown>) => {
    const columnsWithTitleValueChoices = tableSchema?.columns
      .filter((col) => {
        return (
          col.columnConfig.editing?.choices &&
          col.columnConfig.editing?.choices.length > 0 &&
          isTitleValueObject(col.columnConfig.editing?.choices[0])
        )
      })
      .map((col) => ({ id: col.id, choices: col.columnConfig.editing?.choices as JsonFormEnumOptionV2[] }))
    const transformedData = Object.entries(data).reduce(
      (acc, [key, value]) => {
        const colWithChoices = columnsWithTitleValueChoices?.find((col) => col.id === key)
        if (colWithChoices) {
          return {
            ...acc,
            [key]: colWithChoices.choices.find((choice) => choice.value === value),
          }
        }
        return {
          ...acc,
          [key]: value,
        }
      },
      {} as Record<string, unknown>
    )
    console.log('transformedData', transformedData)
    meta?.updateGroup(id, transformedData)
  }

  // Don't render component if essential data is missing
  if (!tableSchema) {
    return null
  }

  if (!handlerId) {
    console.warn('No handlerId provided for edit action:', action)
    return null
  }

  const formSchema = tableToFormSchema(tableSchema, existingEntry as Record<string, unknown>)

  return (
    <SheetWithFormSchema
      wide
      key={action.id}
      triggerTooltip={action.label}
      trigger={
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem key={action.id} onSelect={(e) => e.preventDefault()}>
            <div className="flex items-center gap-2">
              <DynamicIcon
                iconName={action.icon || ''}
                lazyLoad={false}
                className="size-3"
                fallback={<IconEditOutline className="size-3" />}
              />
              <span>{action.label}</span>
            </div>
          </DropdownMenuItem>
        </div>
      }
      schema={formSchema}
      onSubmit={(data: InferFormValues<typeof formSchema>, onSuccess) => {
        const dataToSubmit = { ...info.row.original, ...data }

        // Special handling for group rows: update local state instead of submitting to backend
        if (isGroup && updateGroup) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updateGroup((info.row.original as any).id, data)
          if (onSuccess) onSuccess()
          return
        }

        // Call submitForm with the data and pass the onSuccess callback to be executed only on success
        submitForm(dataToSubmit, {
          onSuccess: () => {
            if (onSuccess) {
              onSuccess()
            }
          },
        })
      }}
      isSubmitting={isSubmitting}
      closeOnSuccess
    />
  )
}
